import React, { useMemo } from "react";
import { Entity } from "@common/types/entity";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { AlertIcon } from "@icons";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import {
    Section,
    SectionedCheckboxTable,
} from "@design-system/SectionedCheckboxTable/SectionedCheckboxTable";

interface EntityRowProps {
    entity: Pick<Entity, "id" | "name" | "workspaceId" | "iconColor" | "logo">;
    errorMessages: Array<{ entityId: number; message: string }>;
}

const EntityRow: React.FC<EntityRowProps> = ({ entity, errorMessages }) => (
    <VStack>
        <HStack gap={8} align="center">
            <EntityAvatar entity={entity} size="xs" aria-hidden />
            {entity.name}
            {errorMessages.length > 0 && (
                <Tooltip content={errorMessages[0].message}>
                    <span>
                        <AlertIcon className="icon-color-yellow-500" />
                    </span>
                </Tooltip>
            )}
        </HStack>
    </VStack>
);

interface BusinessEntitiesSectionedCheckboxTableProps {
    businessEntities: Array<
        Pick<Entity, "id" | "name" | "workspaceId" | "iconColor" | "logo">
    >;
    messages?: Array<{ entityId: number; message: string }>;
    value: number[];
    onChange: (values: number[]) => void;
    searchable?: boolean;
    className?: string;
}

export const BusinessEntitiesSectionedCheckboxTable: React.FC<
    BusinessEntitiesSectionedCheckboxTableProps
> = ({
    businessEntities,
    messages = [],
    value,
    onChange,
    searchable = false,
    className,
}) => {
    const sections: Array<Section<number>> = useMemo(() => {
        if (businessEntities.length === 0) {
            return [];
        }

        return [
            {
                title: "Business",
                items: businessEntities.map((businessEntity) => {
                    const errorMessages = messages.filter(
                        ({ entityId }) => entityId === businessEntity.id,
                    );

                    return {
                        value: businessEntity.id,
                        render: () => (
                            <EntityRow
                                entity={businessEntity}
                                errorMessages={errorMessages}
                            />
                        ),
                        searchValue: businessEntity.name ?? undefined,
                    };
                }),
                allowSelectAll: true,
            },
        ];
    }, [businessEntities, messages]);

    return (
        <SectionedCheckboxTable
            sections={sections}
            value={value}
            onChange={onChange}
            searchable={searchable}
            className={className}
        />
    );
};
